import { IResultData } from "@/domain/entities/shared/result";
import {
  ISurvey,
  ISurveyTargetLine,
} from "@/domain/entities/survey/survey.types";
import TYPES from "@/ioc/identifiers";
import { SERVICE_UID, SurveyTypeCode } from "@/types";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { inject, injectable } from "inversify";
import { combine, Result } from "neverthrow";
import { IBaseParser, IBaseResponse } from "../shared/baseParser";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { IHttpService } from "../shared/httpService";
import {
  HttpSurveyListResponse,
  ISurveyParser,
  HttpSurveyResponse,
  ISurveyTargetLineParser,
  HttpSurveyTargetLineResponse,
  ISurveyRepository,
  HttpLineDemandSurveyListResponse,
  ILineDemandSurveyListParser,
  HttpLineDemandSurveyAnswerResponse,
  ILineDemandSurveyAnswerParser,
} from "./surveyRepository.types";
import {
  IAnswerProp,
  ILineDemandSurvey,
  ILineDemandSurveyAnswer,
} from "@/domain/entities/survey";

@injectable()
export class SurveyRepository implements ISurveyRepository {
  @inject(TYPES.ISurveyParser)
  private surveyParser!: ISurveyParser;

  @inject(TYPES.ISurveyTargetLineParser)
  private survetTargetLineParser!: ISurveyTargetLineParser;

  @inject(TYPES.ILineDemandSurveyListParser)
  private lineDemandSurveyListParser!: ILineDemandSurveyListParser;

  @inject(TYPES.ILineDemandSurveyAnswerParser)
  private lineDemandSurveyAnswerParser!: ILineDemandSurveyAnswerParser;

  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/service/${SERVICE_UID}`
    );
  }

  async getWithdrawalSurveyContents(): Promise<
    Result<ISurvey, ParseError | HttpError>
  > {
    const parseTo = (response: HttpSurveyResponse) => {
      return this.surveyParser.toDomain(response.data);
    };
    return await this.httpService.get<HttpSurveyResponse, ISurvey>(
      {
        url: "/survey/single",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            surveyTypeCode: "SVT-LEAVE",
          },
        },
      },
      { parseTo }
    );
  }

  async getComplaintsSurveyContents(): Promise<
    Result<ISurvey, ParseError | HttpError>
  > {
    const parseTo = (response: HttpSurveyListResponse) => {
      return this.surveyParser.toDomain(response.data[0]);
    };
    return await this.httpService.get<HttpSurveyListResponse, ISurvey>(
      {
        url: "/survey",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            surveyTypeCode: "SVT-REVIEW",
            fromOnDate: "2022-09-01",
          },
        },
      },
      { parseTo }
    );
  }

  async requestWitdrawal(
    surveyId: string,
    answers: Map<number, Map<string, string>>
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const data = this.surveyParser.fromDomain(answers, surveyId);
    const isSurveyAnswerExisted = data.answers?.length && data.surveyUid;

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/member",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          data: isSurveyAnswerExisted ? data : undefined,
        },
      },
      { parseTo }
    );
  }

  async sendSurveyRequest(
    surveyId: string,
    answers: Map<number, Map<string, string | string[]>>,
    turnId?: string,
    allocUid?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    let url = `/survey/${surveyId}/answer`;
    url += turnId ? `/line/turn/${turnId}` : "";

    const data = this.surveyParser.fromDomain(answers, undefined, allocUid);

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url,
        data: { ...data, allocUid },
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getServiceEvaluationContents(
    surveyTypeCode = SurveyTypeCode.BOARD
  ): Promise<Result<ISurvey, ParseError | HttpError>> {
    const parseTo = (response: HttpSurveyListResponse) => {
      return this.surveyParser.toDomain(response.data[0]);
    };
    return await this.httpService.get<HttpSurveyListResponse, ISurvey>(
      {
        url: "/survey",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            surveyTypeCode,
          },
        },
      },
      { parseTo }
    );
  }

  async getSurveyTargetLines(
    surveyId: string
  ): Promise<Result<ISurveyTargetLine[], ParseError | HttpError>> {
    const parseTo = (response: HttpSurveyTargetLineResponse) => {
      const surveyLines =
        response.data?.map(this.survetTargetLineParser.toDomain) ?? [];
      return combine(surveyLines);
    };
    return await this.httpService.get<
      HttpSurveyTargetLineResponse,
      ISurveyTargetLine[]
    >(
      {
        url: `/survey/${surveyId}/target-line`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getLineDemandSurveyList(): Promise<
    Result<ILineDemandSurvey[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpLineDemandSurveyListResponse) => {
      return this.lineDemandSurveyListParser.toDomain(response);
    };
    return await this.httpService.get<
      HttpLineDemandSurveyListResponse,
      ILineDemandSurvey[]
    >(
      {
        url: "/survey",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            surveyTypeCode: "SVT-DEMAND",
          },
        },
      },
      { parseTo }
    );
  }

  async getLineDemandSurveyAnswer(
    surveyUid: string
  ): Promise<
    Result<ILineDemandSurveyAnswer | undefined, ParseError | HttpError>
  > {
    const parseTo = (response: HttpLineDemandSurveyAnswerResponse) => {
      return this.lineDemandSurveyAnswerParser.toDomain(response.data);
    };
    return await this.httpService.get<
      HttpLineDemandSurveyAnswerResponse,
      ILineDemandSurveyAnswer | undefined
    >(
      {
        url: `/survey/auth/${surveyUid}/answer`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async updateLineDemandSurveyAnswer(
    surveyId: string,
    answerProps: IAnswerProp[]
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const answers = this.lineDemandSurveyAnswerParser.fromDomain(answerProps);

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: `/survey/${surveyId}/answer`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: { answers },
      },
      { parseTo }
    );
  }

  async deleteLineDemandSurveyAnswer(
    surveyId: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: `/survey/auth/${surveyId}/answer`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }
}
